import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import API from '../utils/api';

const AuthContext = createContext(null);

// Setup axios interceptor on load
const storedToken = localStorage.getItem('session_token');
if (storedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage immediately
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user_data');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(!localStorage.getItem('user_data'));

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers
      });
      // Update stored data
      localStorage.setItem('user_data', JSON.stringify(response.data));
      setUser(response.data);
    } catch {
      // Only clear if we had a token (meaning it expired/invalid)
      if (token) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user_data');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers });
    } catch {
      // silenced
    }
    localStorage.removeItem('session_token');
    localStorage.removeItem('user_data');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    if (userData?.session_token) {
      localStorage.setItem('session_token', userData.session_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${userData.session_token}`;
    }
    // Save user data without session_token
    const { session_token, ...userOnly } = userData || {};
    localStorage.setItem('user_data', JSON.stringify(userOnly));
    setUser(userOnly);
  }, []);

  const value = useMemo(() => ({
    user, loading, login, logout, updateUser, checkAuth
  }), [user, loading, login, logout, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
